import { useEffect, useState } from "react";

type ApiFunction<T> = () => Promise<T>;

/**
 * Custom hook to handle API polling with a specified delay.
 *
 * @template T - The type of the data returned by the API function.
 * @param {ApiFunction<T>} apiFunction - The API function to be called for polling.
 * @param {number} delay - The delay in milliseconds between each poll.
 * @param {boolean} requestOnlyOnce - If true, the API function will be called only once.
 * @returns {{ data: T | undefined, forceRefresh: () => void }} - An object containing the data returned by the API function and a function to force a refresh.
 *
 * @example
 * const { data, forceRefresh } = useApiPolling(fetchData, 5000, false);
 *
 * @remarks
 * - The hook will poll the API function up to a maximum of 20 times unless `requestOnlyOnce` is set to true.
 * - The delay between polls can be adjusted, with a minimum delay of 40 milliseconds.
 * - The `forceRefresh` function can be used to immediately trigger a new API call.
 * - If `requestOnlyOnce` is true, the API function will be called only once regardless of the delay.
 *
 * @throws Will log an error to the console if the API function call fails.
 */
export function useApiPolling<T>(apiFunction: ApiFunction<T>, delay: number, requestOnlyOnce: boolean): { data: T | undefined, forceRefresh: () => void } {
	const MAX_REQUEST_COUNT = 20;
	const MIN_DELAY = 40;
	const [data, setData] = useState<T | undefined>(undefined);
	const [requestCount, setRequestCount] = useState(requestOnlyOnce ? 1 : MAX_REQUEST_COUNT);
	const [forceRefresh, setForceRefresh] = useState(false);

	useEffect(() => {
		if (requestCount < 1) {
			return;
		}

		const fetchData = async () => {
			try {
				const response = await apiFunction();
				setData(response);
				setRequestCount(prevCount => prevCount - 1);
			} catch (error) {
				console.error(error);
			}
		};
		
		// calculate the delay
		const calculatedDelay = requestCount === MAX_REQUEST_COUNT || requestOnlyOnce || forceRefresh
			? MIN_DELAY
			: delay;

		const intervalId = setInterval(fetchData, calculatedDelay);

		if (forceRefresh) {
			setForceRefresh(false);
		}

		return () => clearInterval(intervalId);
	}, [apiFunction, delay, requestCount, forceRefresh, requestOnlyOnce]);

	const triggerForceRefresh = () => {
		if (!requestOnlyOnce) {
			setForceRefresh(true);
			setRequestCount(MAX_REQUEST_COUNT);
		}
	};

	return {data, forceRefresh: triggerForceRefresh};
}